/** Shared defaults + helpers for Enterprise kiosk landing configs. */

export const KIOSK_PAGE_KEYS = {
  FACE_IDENTITY: 'face_identity',
  SALON_ENTRY: 'salon_entry',
};

export const BLEND_MODES = [
  'normal',
  'multiply',
  'screen',
  'overlay',
  'soft-light',
  'hard-light',
  'darken',
  'lighten',
];

export const CTA_VARIANTS = ['pill', 'filled', 'outline'];
export const CTA_FILL_MODES = ['solid', 'gradient'];
export const CTA_SHADOWS = ['none', 'soft', 'strong'];
export const CTA_FONT_WEIGHTS = ['medium', 'semibold', 'bold'];
export const CTA_LETTER_SPACINGS = ['tight', 'normal', 'wide'];

/** Legacy Nxt CRM fields — excluded from SaaS register forms. */
export const NXT_CRM_REGISTER_TYPES = new Set(['membershipTier', 'allergies', 'preferenceNotes']);

/** Register form field types for Face Identity / Salon enroll pages. */
export const REGISTER_FIELD_TYPES = [
  'name',
  'mobile',
  'email',
  'gender',
  'dateOfBirth',
  'anniversaryDate',
  'skinType',
  'text',
];

/** How register fields render on enroll. */
export const REGISTER_INPUT_KINDS = ['text', 'date', 'textarea', 'yesno', 'options'];

/** Default gender choices when the Gender field uses the Options input style. */
export const DEFAULT_GENDER_OPTIONS = [
  { label: 'Male', iconUrl: '' },
  { label: 'Female', iconUrl: '' },
  { label: 'Transgender', iconUrl: '' },
];

export const LOCKED_REGISTER_TYPES = new Set(); // all fields removable / re-addable
export const MAX_REGISTER_FIELDS = 12;

/** Types that support choosing input style in the builder (most types). */
export const REGISTER_FLEX_INPUT_TYPES = new Set([
  'name',
  'mobile',
  'email',
  'gender',
  'dateOfBirth',
  'anniversaryDate',
  'skinType',
  'text',
]);

export const REGISTER_FIELD_TYPE_META = [
  { type: 'name', label: 'Name', key: 'name', defaultInputKind: 'text' },
  { type: 'mobile', label: 'Mobile number', key: 'mobile', defaultInputKind: 'text' },
  { type: 'email', label: 'Email', key: 'email', defaultInputKind: 'text' },
  { type: 'gender', label: 'Gender', key: 'gender', defaultInputKind: 'options' },
  { type: 'dateOfBirth', label: 'DOB', key: 'dateOfBirth', defaultInputKind: 'date' },
  { type: 'anniversaryDate', label: 'Anniversary date', key: 'anniversaryDate', defaultInputKind: 'date' },
  { type: 'skinType', label: 'Skin type', key: 'skinType', defaultInputKind: 'text' },
  { type: 'text', label: 'Custom field', key: null, defaultInputKind: 'text' },
];

/** Known fields offered in the Add field menu. */
export const REGISTER_ADD_KNOWN_OPTIONS = [
  { type: 'name', label: 'Name', inputKind: 'text' },
  { type: 'mobile', label: 'Mobile number', inputKind: 'text' },
  { type: 'email', label: 'Email', inputKind: 'text' },
  { type: 'gender', label: 'Gender', inputKind: 'options' },
  { type: 'dateOfBirth', label: 'DOB', inputKind: 'date' },
  { type: 'anniversaryDate', label: 'Anniversary date', inputKind: 'date' },
  { type: 'skinType', label: 'Skin type', inputKind: 'text' },
];

/** Options shown when adding a fully custom field (label set by admin). */
export const REGISTER_ADD_CUSTOM_OPTIONS = [
  { inputKind: 'text', label: 'Text field' },
  { inputKind: 'date', label: 'Date field' },
  { inputKind: 'textarea', label: 'Longer text' },
  { inputKind: 'yesno', label: 'Yes / No' },
  { inputKind: 'options', label: 'Options (with icons)' },
];

/** CRM class name follows the display label (for transfer to another CRM). */
export function labelToClassName(label) {
  return String(label || '').trim();
}

export function defaultInputKindForType(type, explicit) {
  if (type === 'membershipTier') return 'yesno';
  if (type === 'gender') return 'options';
  if (REGISTER_INPUT_KINDS.includes(explicit)) return explicit;
  const meta = REGISTER_FIELD_TYPE_META.find((item) => item.type === type);
  if (meta?.defaultInputKind) return meta.defaultInputKind;
  if (type === 'dateOfBirth' || type === 'anniversaryDate') return 'date';
  if (type === 'preferenceNotes') return 'textarea';
  return 'text';
}

/** Allowed input styles for a field type in the Common register form builder. */
export function allowedInputKindsForType(type) {
  if (type === 'membershipTier') return ['yesno'];
  if (type === 'gender') return ['options'];
  if (type === 'dateOfBirth' || type === 'anniversaryDate') return ['date', 'text'];
  if (type === 'mobile') return ['text'];
  if (type === 'name' || type === 'email') return ['text', 'textarea'];
  if (type === 'preferenceNotes') return ['textarea', 'text'];
  return [...REGISTER_INPUT_KINDS];
}

export function inputKindLabel(kind) {
  if (kind === 'textarea') return 'Longer text';
  if (kind === 'date') return 'Date';
  if (kind === 'yesno') return 'Yes / No';
  if (kind === 'options') return 'Options (with icons)';
  return 'Text';
}

export function defaultOptionsForFieldType(type) {
  if (type === 'gender') {
    return DEFAULT_GENDER_OPTIONS.map((item) => ({ ...item }));
  }
  return [
    { label: 'Option 1', iconUrl: '' },
    { label: 'Option 2', iconUrl: '' },
  ];
}

export function normalizeFieldOptions(raw, type) {
  const fallback = defaultOptionsForFieldType(type);
  if (!Array.isArray(raw) || !raw.length) return fallback;
  const out = raw
    .map((item) => {
      if (typeof item === 'string') {
        const label = String(item).trim();
        return label ? { label, iconUrl: '' } : null;
      }
      if (item && typeof item === 'object') {
        const label = String(item.label ?? item.value ?? '').trim();
        if (!label) return null;
        return { label, iconUrl: String(item.iconUrl || item.icon_url || '').trim() };
      }
      return null;
    })
    .filter(Boolean);
  return out.length ? out : fallback;
}

/** Built-in gender artwork when no custom icon is uploaded. */
export function resolveOptionIconUrl(option, fieldType) {
  const custom = String(option?.iconUrl || '').trim();
  if (custom) return custom;
  if (fieldType === 'gender') {
    const label = String(option?.label || '').trim();
    if (!label) return '';
    const file = label === 'Transgender' ? 'trans.png' : `${label.toLowerCase()}.png`;
    return `${process.env.PUBLIC_URL || ''}/gender/${file}`;
  }
  return '';
}

function newRegisterFieldId(prefix = 'f') {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function createRegisterField(type, overrides = {}) {
  const meta = REGISTER_FIELD_TYPE_META.find((item) => item.type === type) || {
    type: 'text',
    label: 'Custom field',
    key: null,
    defaultInputKind: 'text',
  };
  const id = overrides.id || newRegisterFieldId(type === 'text' ? 'custom' : type);
  const key =
    type === 'text'
      ? overrides.key || `custom_${id}`
      : meta.key || overrides.key || type;
  const label = overrides.label != null ? String(overrides.label) : meta.label || '';
  const resolvedKind = defaultInputKindForType(type, overrides.inputKind);
  const allowed = allowedInputKindsForType(type);
  const inputKind = allowed.includes(resolvedKind) ? resolvedKind : allowed[0] || resolvedKind;
  const className =
    overrides.className != null && String(overrides.className).trim()
      ? String(overrides.className).trim()
      : labelToClassName(label);
  const field = {
    id,
    key,
    type: REGISTER_FIELD_TYPES.includes(type) ? type : 'text',
    label,
    className,
    placeholder: overrides.placeholder || '',
    required: Boolean(overrides.required),
    enabled: overrides.enabled !== false,
    inputKind,
  };
  if (inputKind === 'options' || type === 'gender') {
    field.inputKind = 'options';
    field.options = normalizeFieldOptions(overrides.options, type);
  }
  return field;
}

export function defaultRegisterForm() {
  return {
    title: 'Create your profile',
    subtitle: 'We could not match this face yet. Complete your details to enroll.',
    fields: [
      createRegisterField('name', {
        id: 'f_name',
        label: 'Name',
        required: true,
        enabled: true,
        placeholder: 'Full name',
      }),
      createRegisterField('mobile', {
        id: 'f_mobile',
        label: 'Mobile number',
        required: true,
        enabled: true,
        placeholder: 'Mobile number with country code',
      }),
      createRegisterField('dateOfBirth', {
        id: 'f_dob',
        label: 'DOB',
        required: false,
        enabled: true,
      }),
    ],
  };
}

export function normalizeRegisterForm(raw) {
  const base = defaultRegisterForm();
  const src = raw && typeof raw === 'object' ? raw : {};
  // Empty list falls back to defaults (Name, Mobile number, DOB).
  const fieldsIn = Array.isArray(src.fields)
    ? src.fields.length
      ? src.fields
      : base.fields
    : base.fields;
  const seenKeys = new Set();
  const fields = [];

  fieldsIn.forEach((item, idx) => {
    if (!item || typeof item !== 'object') return;
    if (fields.length >= MAX_REGISTER_FIELDS) return;
    const rawType = String(item.type || 'text').trim();
    if (NXT_CRM_REGISTER_TYPES.has(rawType)) return;
    const type = REGISTER_FIELD_TYPES.includes(rawType) ? rawType : 'text';
    const meta = REGISTER_FIELD_TYPE_META.find((m) => m.type === type);
    if (type !== 'text' && fields.some((f) => f.type === type)) return;
    const id =
      String(item.id || (type === 'text' ? `custom_${idx}` : `f_${type}`)).trim() ||
      `f_${type}_${idx}`;
    let key =
      type === 'text'
        ? String(item.key || `custom_${id}`).trim() || `custom_${id}`
        : meta?.key || type;
    if (seenKeys.has(key)) {
      if (type !== 'text') return;
      key = `custom_${id}`;
    }
    seenKeys.add(key);
    const label = String(item.label != null ? item.label : meta?.label || '').trim();
    const resolvedLabel = label || meta?.label || 'Field';
    const classNameRaw = String(item.className != null ? item.className : '').trim();
    const inputKind = defaultInputKindForType(type, item.inputKind);
    const allowed = allowedInputKindsForType(type);
    const resolvedInputKind = allowed.includes(inputKind) ? inputKind : allowed[0] || inputKind;
    const nextField = {
      id,
      key,
      type,
      label: resolvedLabel,
      className: classNameRaw || labelToClassName(resolvedLabel),
      placeholder: String(item.placeholder || '').trim(),
      required: Boolean(item.required),
      enabled: item.enabled !== false,
      inputKind: resolvedInputKind,
    };
    if (resolvedInputKind === 'options' || type === 'gender') {
      nextField.inputKind = 'options';
      nextField.options = normalizeFieldOptions(item.options, type);
    }
    fields.push(nextField);
  });

  if (!fields.some((f) => f.type === 'mobile')) {
    const mobile = createRegisterField('mobile', {
      id: 'f_mobile',
      label: 'Mobile number',
      required: true,
      enabled: true,
      placeholder: 'Mobile number with country code',
    });
    const nameIdx = fields.findIndex((f) => f.type === 'name');
    fields.splice(nameIdx >= 0 ? nameIdx + 1 : 0, 0, mobile);
  }
  fields.forEach((f) => {
    if (f.type !== 'mobile') return;
    f.required = true;
    f.enabled = true;
    f.inputKind = 'text';
    if (!String(f.placeholder || '').trim()) f.placeholder = 'Mobile number with country code';
  });
  while (fields.length > MAX_REGISTER_FIELDS) {
    const dropIdx = [...fields]
      .map((f, i) => i)
      .reverse()
      .find((i) => fields[i].type !== 'name' && fields[i].type !== 'mobile');
    if (dropIdx == null) break;
    fields.splice(dropIdx, 1);
  }

  return {
    title: String(src.title != null ? src.title : base.title).trim() || base.title,
    subtitle: String(src.subtitle != null ? src.subtitle : base.subtitle),
    fields: fields.slice(0, MAX_REGISTER_FIELDS),
  };
}

export function enabledRegisterFields(registerForm) {
  const form = normalizeRegisterForm(registerForm);
  return form.fields.filter((f) => f.enabled !== false);
}

export function defaultTermsAndConditions() {
  return {
    title: 'Terms and Conditions',
    body:
      '<p>By using this kiosk you agree to Enterprise’ terms for face recognition check-in, data processing under applicable Indian law (including the Digital Personal Data Protection Act, 2023), and salon service personalization. Please ask a staff member if you have questions before continuing.</p>',
  };
}

export function normalizeTermsAndConditions(raw) {
  const base = defaultTermsAndConditions();
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    title: String(src.title != null ? src.title : base.title).trim() || base.title,
    body: String(src.body != null ? src.body : base.body),
  };
}

export function defaultCtaStyle() {
  return {
    text: 'Proceed',
    variant: 'pill',
    fillMode: 'solid',
    backgroundColor: '#c5a059',
    gradientFrom: '#f3e0b4',
    gradientTo: '#9e7436',
    gradientAngle: 135,
    textColor: '#241608',
    borderColor: '#c5a059',
    shadow: 'soft',
    uppercase: true,
    showArrow: true,
    fontWeight: 'bold',
    letterSpacing: 'wide',
  };
}

/** Inline styles for CTA fill + typography from config. */
export function buildCtaStyle(cta = {}) {
  const fillMode = CTA_FILL_MODES.includes(cta.fillMode) ? cta.fillMode : 'solid';
  const variant = CTA_VARIANTS.includes(cta.variant) ? cta.variant : 'pill';
  const isOutline = variant === 'outline';
  const solid = cta.backgroundColor || '#c5a059';
  const from = cta.gradientFrom || '#f3e0b4';
  const to = cta.gradientTo || '#9e7436';
  const angle = Number.isFinite(Number(cta.gradientAngle)) ? Number(cta.gradientAngle) : 135;
  const weightMap = { medium: 500, semibold: 600, bold: 700 };
  const spacingMap = { tight: '0.04em', normal: '0.1em', wide: '0.16em' };
  const fontWeight = weightMap[cta.fontWeight] || weightMap.bold;
  const letterSpacing = spacingMap[cta.letterSpacing] || spacingMap.wide;
  const textColor = cta.textColor || '#241608';
  const borderColor = cta.borderColor || solid;

  let background = solid;
  let backgroundImage = 'none';
  if (isOutline) {
    background = 'transparent';
    backgroundImage = 'none';
  } else if (fillMode === 'gradient') {
    backgroundImage = `linear-gradient(${angle}deg, ${from}, ${to})`;
    background = from;
  }

  return {
    background,
    backgroundImage,
    backgroundColor: isOutline ? 'transparent' : fillMode === 'gradient' ? from : solid,
    color: textColor,
    borderColor,
    borderStyle: 'solid',
    borderWidth: '1px',
    fontWeight,
    letterSpacing,
    textTransform: cta.uppercase === false ? 'none' : 'uppercase',
    // CSS vars beat hardcoded .fi-lux__cta gradient in FaceIdentityLandingPage.css
    '--kiosk-cta-bg': isOutline ? 'transparent' : solid,
    '--kiosk-cta-bg-image': backgroundImage === 'none' ? 'none' : backgroundImage,
    '--kiosk-cta-color': textColor,
    '--kiosk-cta-border': borderColor,
  };
}

export const ICON_KEYS = [
  { key: 'care', label: 'Expert care' },
  { key: 'personalized', label: 'Personalized' },
  { key: 'premium', label: 'Premium' },
  { key: 'trusted', label: 'Trusted' },
  { key: 'leaf', label: 'Leaf' },
  { key: 'spark', label: 'Spark' },
];

export const SIZING_FIELDS = [
  { key: 'logoScale', label: 'Logo size' },
  { key: 'taglineScale', label: 'Brand tagline' },
  { key: 'titleScale', label: 'Title' },
  { key: 'subtitleScale', label: 'Subtitle' },
  { key: 'bodyScale', label: 'Body text' },
  { key: 'cardTitleScale', label: 'Card title' },
  { key: 'cardTextScale', label: 'Card text' },
  { key: 'cardIconScale', label: 'Card icon' },
  { key: 'consentScale', label: 'Consent text' },
  { key: 'ctaScale', label: 'CTA button' },
  { key: 'footerScale', label: 'Footer tagline' },
  { key: 'contentWidthScale', label: 'Content width' },
  { key: 'spacingScale', label: 'Section spacing' },
];

export function defaultSizing() {
  return Object.fromEntries(SIZING_FIELDS.map(({ key }) => [key, 100]));
}

/** Per-content text colors for kiosk landings (builder + live). */
export function defaultTextColors() {
  return {
    title: '#d4af73',
    subtitle: '#6b4c9a',
    body: '#3a3428',
    footer: '#6b4c9a',
    brandTagline: '#9e7436',
    cardTitle: '#6b4c9a',
    cardText: '#3a3428',
    consent: '#6b6358',
  };
}

export function normalizeTextColors(raw) {
  const base = defaultTextColors();
  const src = raw && typeof raw === 'object' ? raw : {};
  const out = { ...base };
  Object.keys(base).forEach((key) => {
    const hex = String(src[key] != null ? src[key] : '').trim();
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) out[key] = hex;
  });
  return out;
}

/** CSS custom properties for landing text colors. */
export function buildTextColorsStyle(textColors = {}) {
  const t = normalizeTextColors(textColors);
  return {
    '--kiosk-text-title': t.title,
    '--kiosk-text-subtitle': t.subtitle,
    '--kiosk-text-body': t.body,
    '--kiosk-text-footer': t.footer,
    '--kiosk-text-brand-tagline': t.brandTagline,
    '--kiosk-text-card-title': t.cardTitle,
    '--kiosk-text-card-text': t.cardText,
    '--kiosk-text-consent': t.consent,
  };
}

export const SECTION_ORDER_KEYS = [
  { key: 'brand', label: 'Brand (logo + tagline)' },
  { key: 'title', label: 'Title' },
  { key: 'subtitle', label: 'Subtitle' },
  { key: 'body', label: 'Body text' },
  { key: 'cards', label: 'Feature cards' },
  { key: 'consent', label: 'Consent' },
  { key: 'cta', label: 'CTA button' },
  { key: 'footer', label: 'Footer tagline' },
];

export function defaultSectionOrder() {
  return SECTION_ORDER_KEYS.map(({ key }) => key);
}

/** Show/hide map for every landing block (preview + live). */
export function defaultSectionsVisible(overrides = {}) {
  const base = Object.fromEntries(SECTION_ORDER_KEYS.map(({ key }) => [key, true]));
  return { ...base, ...(overrides || {}) };
}

export function normalizeSectionsVisible(raw, legacy = {}) {
  const base = defaultSectionsVisible();
  // Migrate older configs that only had cardsEnabled / consent.enabled.
  if (legacy.cardsEnabled === false) base.cards = false;
  if (legacy.consentEnabled === false) base.consent = false;
  const src = raw && typeof raw === 'object' ? raw : {};
  const out = { ...base };
  SECTION_ORDER_KEYS.forEach(({ key }) => {
    if (Object.prototype.hasOwnProperty.call(src, key)) {
      out[key] = src[key] !== false;
    }
  });
  return out;
}

export function isSectionVisible(config, key) {
  const legacy = {
    cardsEnabled: config?.cardsEnabled,
    consentEnabled: config?.consent?.enabled,
  };
  const vis = normalizeSectionsVisible(config?.sectionsVisible, legacy);
  return vis[key] !== false;
}

export function normalizeSectionOrder(order) {
  const allowed = new Set(defaultSectionOrder());
  const seen = new Set();
  const out = [];
  if (Array.isArray(order)) {
    order.forEach((key) => {
      if (allowed.has(key) && !seen.has(key)) {
        out.push(key);
        seen.add(key);
      }
    });
  }
  defaultSectionOrder().forEach((key) => {
    if (!seen.has(key)) out.push(key);
  });
  return out;
}

export function defaultFaceIdentityConfig() {
  return {
    version: 1,
    background: {
      gradientFrom: '#faf4eb',
      gradientTo: '#f0d9a8',
      gradientAngle: 180,
      gradientBlendStart: 0,
      backdropImageUrl: null,
      backdropBlendMode: 'soft-light',
      backdropOpacity: 0.35,
    },
    brand: {
      showLogo: true,
      showTagline: true,
      logoUrl: null,
      logoOffsetX: 0,
      tagline: "India's No.1 Hair and Beauty Salon",
    },
    sizing: defaultSizing(),
    sectionOrder: defaultSectionOrder(),
    sectionsVisible: defaultSectionsVisible({ consent: false }),
    swatches: defaultSwatches(),
    textColors: defaultTextColors(),
    title: 'Welcome',
    subtitle: 'Guest recognition',
    body:
      "Welcome to Enterprise. We're delighted to have you here. Sit back, relax, and enjoy a premium salon experience thoughtfully crafted to help you look and feel your absolute best.",
    cardsEnabled: true,
    cards: [
      {
        id: 'c1',
        title: 'Expert Beauty Care',
        text: 'Guided by skilled stylists who understand your unique beauty needs.',
        iconKey: 'care',
      },
      {
        id: 'c2',
        title: 'Personalized Experience',
        text: 'Every visit is informed by your profile, preferences, and history.',
        iconKey: 'personalized',
      },
      {
        id: 'c3',
        title: 'Premium Salon Experience',
        text: 'A refined check-in that sets the tone for attentive, elevated care.',
        iconKey: 'premium',
      },
      {
        id: 'c4',
        title: 'Trusted Across India',
        text: 'Part of a network loved by guests for consistent, quality beauty care.',
        iconKey: 'trusted',
      },
    ],
    consent: {
      enabled: false,
      checkboxLabel: 'I agree to the face scan consent',
      text:
        'By proceeding, you consent to collection and processing of your face data for identity check-in and service personalization under the Digital Personal Data Protection Act, 2023 and applicable Indian law.',
      includeTerms: true,
      termsLinkLabel: 'Terms and Conditions',
    },
    cta: {
      ...defaultCtaStyle(),
      text: 'Proceed',
    },
    entryOptions: {
      /** When false: Face Identity /start skips to scan; Salon /start hides mobile lookup. */
      showMobileLookup: false,
    },
    registerForm: defaultRegisterForm(),
    termsAndConditions: defaultTermsAndConditions(),
    footerTagline: 'Your beauty journey, recognized.',
  };
}

export function defaultSalonEntryConfig() {
  return {
    version: 1,
    background: {
      gradientFrom: '#faf4eb',
      gradientTo: '#f0d9a8',
      gradientAngle: 180,
      gradientBlendStart: 0,
      backdropImageUrl: null,
      backdropBlendMode: 'soft-light',
      backdropOpacity: 0.35,
    },
    brand: {
      showLogo: true,
      showTagline: true,
      logoUrl: null,
      logoOffsetX: 0,
      tagline: "India's No.1 Hair and Beauty Salon",
    },
    sizing: defaultSizing(),
    sectionOrder: defaultSectionOrder(),
    sectionsVisible: defaultSectionsVisible({ cards: false, consent: false }),
    swatches: defaultSwatches(),
    textColors: defaultTextColors(),
    title: 'Welcome',
    subtitle: 'Personalized beauty analysis',
    body:
      'Start your salon visit with a quick face check-in, then choose skin analysis or the beauty studio — tailored to your profile in moments.',
    cardsEnabled: false,
    cards: [],
    consent: {
      enabled: false,
      checkboxLabel: 'I agree to the face scan consent',
      text: '',
      includeTerms: true,
      termsLinkLabel: 'Terms and Conditions',
    },
    cta: {
      ...defaultCtaStyle(),
      text: 'Start Salon Check-In',
    },
    entryOptions: {
      showMobileLookup: false,
    },
    registerForm: defaultRegisterForm(),
    termsAndConditions: defaultTermsAndConditions(),
    footerTagline: 'Check in once. Continue your salon journey.',
  };
}

/** Whether start-choice pages show the mobile-number entry option (default off). */
export function showMobileLookupOption(config) {
  return config?.entryOptions?.showMobileLookup === true;
}

export function defaultConfigForKey(pageKey) {
  if (pageKey === KIOSK_PAGE_KEYS.SALON_ENTRY) return defaultSalonEntryConfig();
  return defaultFaceIdentityConfig();
}

/** Per-picker recent swatches (Setflow PageSetup pattern). */
export const SWATCH_KEYS = [
  'gradientFrom',
  'gradientTo',
  'ctaBackground',
  'ctaGradientFrom',
  'ctaGradientTo',
  'ctaText',
  'ctaBorder',
  'textTitle',
  'textSubtitle',
  'textBody',
  'textFooter',
  'textBrandTagline',
  'textCardTitle',
  'textCardText',
  'textConsent',
];

export const MAX_SWATCHES = 8;

export function defaultSwatches() {
  return Object.fromEntries(SWATCH_KEYS.map((key) => [key, []]));
}

export function pushSwatch(list, color, max = MAX_SWATCHES) {
  const hex = String(color || '').trim();
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return Array.isArray(list) ? list : [];
  const prev = Array.isArray(list) ? list : [];
  return [hex, ...prev.filter((c) => String(c).toLowerCase() !== hex.toLowerCase())].slice(0, max);
}

export function normalizeSwatches(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const out = defaultSwatches();
  SWATCH_KEYS.forEach((key) => {
    const list = Array.isArray(src[key]) ? src[key] : [];
    const cleaned = [];
    const seen = new Set();
    list.forEach((value) => {
      const hex = String(value || '').trim();
      if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return;
      const id = hex.toLowerCase();
      if (seen.has(id)) return;
      seen.add(id);
      cleaned.push(hex);
    });
    out[key] = cleaned.slice(0, MAX_SWATCHES);
  });
  return out;
}

export function buildBackgroundStyle(background = {}) {
  const from = background.gradientFrom || '#faf4eb';
  const to = background.gradientTo || '#f0d9a8';
  const angle = Number.isFinite(Number(background.gradientAngle))
    ? Number(background.gradientAngle)
    : 180;
  // Vanish / blend-start: hold `from` until this %, then transition to `to`.
  let blendStart = Number(background.gradientBlendStart);
  if (!Number.isFinite(blendStart)) blendStart = 0;
  blendStart = Math.max(0, Math.min(90, Math.round(blendStart)));
  const gradient =
    blendStart <= 0
      ? `linear-gradient(${angle}deg, ${from}, ${to})`
      : `linear-gradient(${angle}deg, ${from} 0%, ${from} ${blendStart}%, ${to} 100%)`;
  // Set both shorthand + longhand + CSS var so hardcoded .fi-lux layers cannot win.
  const style = {
    background: gradient,
    backgroundImage: gradient,
    backgroundColor: from,
    '--kiosk-page-bg': gradient,
  };
  if (background.backdropImageUrl) {
    style['--kiosk-backdrop-url'] = `url("${background.backdropImageUrl}")`;
    style['--kiosk-backdrop-opacity'] = String(
      Math.max(0, Math.min(1, Number(background.backdropOpacity ?? 0.35))),
    );
    style['--kiosk-backdrop-blend'] = background.backdropBlendMode || 'soft-light';
  }
  return style;
}

function sizingToFactor(value, fallback = 100) {
  const num = Number(value);
  const pct = Number.isFinite(num) ? num : fallback;
  return Math.max(0.4, Math.min(2.2, pct / 100));
}

/** CSS custom properties for logo / typography / layout scales. */
export function buildSizingStyle(sizing = {}) {
  const s = { ...defaultSizing(), ...(sizing || {}) };
  return {
    '--kiosk-logo-scale': String(sizingToFactor(s.logoScale)),
    '--kiosk-tagline-scale': String(sizingToFactor(s.taglineScale)),
    '--kiosk-title-scale': String(sizingToFactor(s.titleScale)),
    '--kiosk-subtitle-scale': String(sizingToFactor(s.subtitleScale)),
    '--kiosk-body-scale': String(sizingToFactor(s.bodyScale)),
    '--kiosk-card-title-scale': String(sizingToFactor(s.cardTitleScale)),
    '--kiosk-card-text-scale': String(sizingToFactor(s.cardTextScale)),
    '--kiosk-card-icon-scale': String(sizingToFactor(s.cardIconScale)),
    '--kiosk-consent-scale': String(sizingToFactor(s.consentScale)),
    '--kiosk-cta-scale': String(sizingToFactor(s.ctaScale)),
    '--kiosk-footer-scale': String(sizingToFactor(s.footerScale)),
    '--kiosk-content-width-scale': String(sizingToFactor(s.contentWidthScale)),
    '--kiosk-spacing-scale': String(sizingToFactor(s.spacingScale)),
  };
}

export function buildLandingRootStyle(config = {}) {
  const brand = config.brand || {};
  const offsetRaw = Number(brand.logoOffsetX);
  const logoOffsetX = Number.isFinite(offsetRaw) ? Math.max(-60, Math.min(60, offsetRaw)) : 0;
  return {
    ...buildBackgroundStyle(config.background || {}),
    ...buildSizingStyle(config.sizing || {}),
    ...buildTextColorsStyle(config.textColors || {}),
    '--kiosk-logo-offset-x': String(logoOffsetX),
  };
}
